'use client';
import { useAppSettings } from '@bells/core';
import { useAuth } from '@bells/core/auth';
import { useLanguage } from '@bells/core/i18n';
import { stringToColor } from '@bells/core/lib/colors';
import { getInitials } from '@bells/core/lib/initals';
import { cn } from '@bells/core/lib/utils';
import {
  reviewDelete,
  reviewEditByAdmin,
  reviewEditMessage,
  reviewStateCheckRequired,
  reviewStateDeclined,
} from '@bells/i18n';
import {
  AttendantReviewResponse,
  ReviewState,
} from '@bells/schema/models/review';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@bells/ui/components/avatar';
import { Badge } from '@bells/ui/components/badge';
import { Button } from '@bells/ui/components/button';
import { CalendarFormat } from '@bells/ui/components/date-formatter';
import { Rating } from '@bells/ui/components/rating';
import { Pencil, Trash } from 'lucide-react';
import { useMemo } from 'react';

export const GirlReview = ({
  item,
  onUpdate,
  onDelete,
}: {
  item: AttendantReviewResponse;
  onUpdate: () => void;
  onDelete: () => void;
}) => {
  const { mediaUrl } = useAppSettings();
  const authorName = useMemo(() => {
    return item.author?.name ?? 'Deleted User';
  }, [item]);
  const { language } = useLanguage();
  const { currentUser, isAuthed } = useAuth();
  return (
    <div
      className='flex border-b py-2 last:border-0'
      itemScope={true}
      itemType='https://schema.org/Review'
      itemProp='review'
    >
      <div className='flex w-full flex-col gap-4'>
        <div className='flex w-full items-center gap-4'>
          <Avatar className='size-10'>
            <AvatarFallback
              className={cn(
                'bg-primary text-xs uppercase text-primary-foreground',
                `bg-${stringToColor(authorName)}`
              )}
            >
              {getInitials(authorName)}
            </AvatarFallback>

            {item.author?.image && (
              <AvatarImage
                src={
                  item.author.image
                    ? `${mediaUrl}/${item.author.image}`
                    : undefined
                }
              ></AvatarImage>
            )}
          </Avatar>

          <div className='flex w-full flex-col gap-1'>
            <span className='w-full truncate pl-1 font-bold' itemProp='author'>
              {authorName}
            </span>
            <div className='flex w-full items-center justify-between'>
              <div
                className='flex items-center gap-2'
                itemProp='reviewRating'
                itemScope
                itemType='https://schema.org/Rating'
              >
                <Rating
                  variant='yellow'
                  totalStars={5}
                  rating={item.totalRating}
                  disabled
                ></Rating>
                <span itemProp='ratingValue' className='text-sm font-bold'>
                  {item.totalRating.toFixed(1)}
                </span>
                <span className='sr-only' itemProp='bestRating'>
                  5.0
                </span>
              </div>
              <CalendarFormat
                className='text-sm font-medium text-muted-foreground'
                date={item.created_at ?? ''}
                locale={language}
              ></CalendarFormat>
            </div>
          </div>
        </div>

        <p itemProp='reviewBody' className='text-paragraph'>
          {item.message}
        </p>

        {isAuthed &&
          currentUser?.id === item.author?.id &&
          item.state === ReviewState.TRANSFORMED && (
            <p className='text-xs text-muted-foreground'>
              {reviewEditByAdmin()}
            </p>
          )}

        {isAuthed && currentUser?.id === item.author?.id && (
          <div className='flex flex-col gap-4 lg:flex-row lg:justify-between'>
            <div className='flex items-center'>
              {item.state === ReviewState.CHECK_REQUIRED && (
                <Badge>{reviewStateCheckRequired()}</Badge>
              )}
              {item.state === ReviewState.DECLINED && (
                <Badge variant={'destructive'}>{reviewStateDeclined()}</Badge>
              )}
            </div>

            <div className='flex flex-wrap gap-4'>
              <Button
                onClick={onDelete}
                size={'xs'}
                variant={'link'}
                className='text-destructive'
              >
                <Trash></Trash> {reviewDelete()}
              </Button>

              {item.state === ReviewState.CHECK_REQUIRED && (
                <Button onClick={onUpdate} variant={'link'} size={'xs'}>
                  <Pencil></Pencil> {reviewEditMessage()}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
